import React from "react";
import { Eye, Pencil, Trash2, Building, Globe, Link2, Loader2 } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Switch } from "src/components/ui/switch";
import { Skeleton } from "src/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "src/components/ui/table";
import { Can } from "src/components/shared/Can";
import { PropertyStatusBadge } from "./PropertyStatusBadge";

function relativeTime(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function SkeletonRow() {
  return (
    <TableRow>
      {[40, 160, 120, 100, 100, 100, 80, 120, 80].map((w, i) => (
        <TableCell key={i}>
          <Skeleton className="h-4 rounded" style={{ width: w }} />
        </TableCell>
      ))}
    </TableRow>
  );
}

function EmptyState({ onNewListing, isRental }) {
  return (
    <TableRow>
      <TableCell colSpan={9}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Building className="h-10 w-10 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-600">
            No properties found
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Get started by adding your first {isRental ? "rental" : "sale"}{" "}
            listing
          </p>
          <Button size="sm" className="mt-4" onClick={onNewListing}>
            + Add New
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function PropertyListingsTable({
  listings = [],
  loading,
  meta,
  page,
  onPageChange,
  onShow,
  onEdit,
  onDelete,
  onNewListing,
  onTogglePublish,
  onCopyLink,
  publishPendingId,
  isRental = false,
}) {
  const startRow = (page - 1) * (meta?.per_page || 20) + 1;
  const endRow = Math.min(startRow + (listings.length - 1), meta?.total || 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead className="min-w-[160px]">Title</TableHead>
            <TableHead className="min-w-[140px]">Contact</TableHead>
            <TableHead className="min-w-[120px]">Location</TableHead>
            <TableHead className="w-[100px]">Type</TableHead>
            <TableHead className="w-[120px]">Price</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead className="w-[150px]">Website</TableHead>
            <TableHead className="w-[100px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
          ) : listings.length === 0 ? (
            <EmptyState onNewListing={onNewListing} isRental={isRental} />
          ) : (
            listings.map((item, idx) => (
              <TableRow key={item.id}>
                <TableCell className="text-xs text-slate-400">
                  {startRow + idx}
                </TableCell>
                <TableCell>
                  <p className="font-medium text-slate-900 text-[13px]">
                    {item.title}
                  </p>
                  {item.configuration && (
                    <p className="text-[11px] text-slate-400 mt-0.5 uppercase">
                      {item.configuration}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <p className="text-[13px] text-slate-700">
                    {item.contact?.name || "—"}
                  </p>
                  {item.contact?.phone && (
                    <p className="text-[11px] text-slate-400">
                      {item.contact.phone}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <p className="text-[13px] text-slate-700 capitalize">
                    {item.locality || item.city || "—"}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-[13px] text-slate-600 capitalize">
                    {item.property_type?.replace(/_/g, " ") || "—"}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-[13px] font-medium text-slate-800">
                    {isRental
                      ? `₹${Number(item.rent_amount || 0).toLocaleString()}/mo`
                      : `₹${Number(item.asking_price || 0).toLocaleString()}`}
                  </p>
                </TableCell>
                <TableCell>
                  <PropertyStatusBadge status={item.status} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Can
                      resource="listings"
                      action="publish"
                      fallback={
                        <span
                          className={`text-[11px] font-medium ${item.published ? "text-emerald-600" : "text-slate-400"}`}
                        >
                          {item.published ? "Live" : "Not live"}
                        </span>
                      }
                    >
                      {publishPendingId === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      ) : (
                        <Switch
                          checked={Boolean(item.published)}
                          onCheckedChange={(val) => onTogglePublish(item, val)}
                          title={item.published ? "Live on website — click to unpublish" : "Publish to website"}
                        />
                      )}
                      <span
                        className={`text-[11px] font-medium ${item.published ? "text-emerald-600" : "text-slate-400"}`}
                      >
                        {item.published ? "Live" : "Draft"}
                      </span>
                    </Can>
                    {item.published && item.public_path && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onCopyLink(item)}
                        title="Copy public link"
                      >
                        <Link2 className="h-3.5 w-3.5 text-slate-400 hover:text-blue-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onShow(item)}
                      title="View details"
                    >
                      <Eye className="h-3.5 w-3.5 text-slate-400 hover:text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(item)}
                      title="Edit listing"
                    >
                      <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(item)}
                      title="Delete listing"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!loading && listings.length > 0 && (
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
          <p className="text-xs text-slate-500">
            Showing {startRow}–{endRow} of {meta?.total ?? 0} listings
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              ← Prev
            </Button>
            <span className="px-3 text-xs text-slate-500">
              {page} / {meta?.total_pages ?? 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= (meta?.total_pages ?? 1)}
              onClick={() => onPageChange(page + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
